// SPDX-FileCopyrightText: 2026 Neptuwunium
//
// SPDX-License-Identifier: EUPL-1.2

/// <reference path='../Math.d.ts' />
/// <reference path='../PalTypes.d.ts' />

import { GeoJSONSource, Map, AttributionControl, NavigationControl, FullscreenControl } from 'maplibre-gl'
import type { LayerSpecification, LngLatLike, SourceSpecification, IControl, DataDrivenPropertyValueSpecification, ResolvedImageSpecification } from 'maplibre-gl'
import type { FeatureCollection, Position, GeoJsonProperties } from 'geojson'

import { VoreCore } from '../Core.js'
import { PalElementType, PalRelicType, PalSpawnedCharacterType } from '../PalEnum.js'

// @ts-expect-error(2307)
import style from 'https://unpkg.com/maplibre-gl@^5.24.0/dist/maplibre-gl.css' with { type: 'css' }
document.adoptedStyleSheets.push(style)

export interface MapData {
	Name: string,
	Texture: string,
	Min: Vector3,
	Max: Vector3,
}

export interface IconSettings {
	IconId?: DataDrivenPropertyValueSpecification<ResolvedImageSpecification>,
	Size?: DataDrivenPropertyValueSpecification<number>,
	Clump?: boolean,
}

export interface MapPoint {
	Coordinate: LngLatLike,
	Group: MapGroup,
	Properties: GeoJsonProperties,
}

export interface MapPoints {
	Group: MapGroup,
	Points: MapPoint[],
}

export interface MapLayer {
	ForceHide: boolean,
	Id: string,
	DisplayName: string,
	Icon: string | null,
	MetaGroup: string | null,
	MetaPriority: number,
	Priority: number
}

export const MapObjectMapping = {
	// DamagableRock0001: ['Stone'],
	DamagableRock0002: ['CopperOre'],
	DamagableRock0003: ['Quartz'],
	DamagableRock0004: ['Coal'],
	DamagableRock0005: ['Pal_crystal_L'],
	DamagableRock0006: ['Sulfur'],
	DamagableRock0017: ['Pal_crystal_S', 'MAPOBJECT_NAME_DamagableRock0005'],
	DamagableRock0018: ['Chromium', 'ITEM_NAME_Chromium'],
	DamagableRock0019: ['RainbowCrystal'],
	DamagableRock0020: ['SkyIslandOre'],
	DamagableRock0021: ['WorldTreeOre', 'ITEM_NAME_WorldTreeOre'],
}

class MapGroup {
	Sources: Record<string, SourceSpecification>
	Layers: Record<string, LayerSpecification & MapLayer>
	Data: MapData

	constructor(core: VoreCore, data: MapData, map: Map, isVisible: boolean, icon: string) {
		this.Data = data
		this.Sources = {
			'': {
				type: 'raster',
				tiles: [core.Data.GetResource(`Map/${data.Texture}/{z}/{x}/{y}`)],
				tileSize: 256,
				scheme: 'tms',
			},
		}

		this.Layers = {
			'': {
				id: `${data.Name}Layer`,
				type: 'raster',
				source: `${data.Name}Source`,
				layout: { visibility: isVisible ? 'visible' : 'none' },
				ForceHide: false,
				Id: data.Name,
				DisplayName: `WORLDMAP_NAME_${data.Name}`,
				Icon: icon,
				MetaGroup: null,
				MetaPriority: -1,
				Priority: -1,
			},
		}

		map.addSource(`${data.Name}Source`, this.Sources['']!)
		map.addLayer(this.Layers['']!)
	}

	CreateSource(map: Map, info: MapLayer, iconSettings?: IconSettings) {
		const sourceName = `${this.Data.Name}${info.Id}Source`

		if (this.Sources[sourceName] !== undefined) {
			return
		}

		console.log(`[MapSystem] creating map feature source ${sourceName}`)
		map.addSource(sourceName, this.Sources[sourceName] = {
			type: 'geojson',
			data: {
				type: 'FeatureCollection',
				features: [],
			},
			cluster: iconSettings?.Clump ?? true,
		})

		const layerName = `${this.Data.Name}${info.Id}Layer`
		const visibility = localStorage.getItem(`Map[${layerName}].Visible`)
		let isVisible = map.getLayoutProperty(this.Layers['']!.id, 'visibility') == 'visible'
		if (visibility !== null && visibility === '0') {
			isVisible = false
		}

		const layer = this.Layers[layerName] = {
			id: layerName,
			type: 'symbol',
			source: sourceName,
			layout: {
				visibility: isVisible ? 'visible' : 'none',
				'icon-image': iconSettings?.IconId ?? info.Id,
				'icon-size': iconSettings?.Size ?? 1.0,
				'icon-allow-overlap': true,
				'icon-ignore-placement': true,
			},
			ForceHide: info.ForceHide || !isVisible,
			Id: info.Id,
			DisplayName: info.DisplayName,
			Icon: info.Icon,
			MetaGroup: info.MetaGroup,
			MetaPriority: info.MetaPriority,
			Priority: info.Priority,
		}
		console.log(`[MapSystem] creating map feature layer ${layerName}`, layer)
		map.addLayer(layer)
	}

	async AddPointToSource(map: Map, id: string, points: MapPoint[]) {
		const sourceName = `${this.Data.Name}${id}Source`
		const source = map.getSource<GeoJSONSource>(sourceName)
		if (source === undefined) {
			console.error(`[MapSystem] source layer ${sourceName} does not exist`)
			return
		}

		let data = await source.getData() as FeatureCollection

		for (const point of points) {
			data.features.push({
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: point.Coordinate as Position
				},
				properties: point.Properties
			})
		}

		console.log(`[MapSystem] added ${points.length} points to ${sourceName}`, data)
		source.setData(data)
	}

	SetVisibility(map: Map, visible: boolean) {
		if (visible) {
			localStorage.setItem(`Map.Last`, this.Data.Name)
		}

		for (const layerName in this.Layers) {
			const layer = this.Layers[layerName]!
			map.setLayoutProperty(layer.id, 'visibility', visible && !layer.ForceHide ? 'visible' : 'none')
		}
	}
}

abstract class MapButtonBase implements IControl {
	Container: HTMLDivElement
	PanelContainer: HTMLDivElement
	Button: HTMLButtonElement
	IsOpen: boolean
	System: MapSystem
	LastGeneration: number

	constructor(icon: string, system: MapSystem) {
		this.IsOpen = false
		this.System = system
		this.LastGeneration = -1

		this.Container = document.createElement('div')
		this.Container.classList.add('maplibregl-ctrl')
		this.Container.classList.add('maplibregl-ctrl-group')
		this.Container.setAttribute('aria-hidden', 'true')

		this.Button = document.createElement('button')
		this.Button.classList.add('vore-mapctrl-button')
		this.Button.setAttribute('aria-hidden', 'true')
		this.Button.addEventListener('click', this.Click.bind(this))

		const span = document.createElement('span')
		span.classList.add('maplibregl-ctrl-icon')
		span.setAttribute('aria-hidden', 'true')
		span.style.backgroundImage = `url(${icon})`
		span.style.backgroundSize = '70%'

		this.PanelContainer = document.createElement('div')
		this.PanelContainer.classList.add('vore-map-panel')
		this.PanelContainer.classList.add('maplibregl-ctrl-group')

		const closeButton = document.createElement('div')
		closeButton.classList.add('vore-map-panel-close')
		closeButton.textContent = '×'
		closeButton.addEventListener('click', this.Close.bind(this))

		this.Button.appendChild(span)
		this.Container.appendChild(this.Button)
		document.body.appendChild(this.PanelContainer)
		this.PanelContainer.appendChild(closeButton)
	}

	BuildElement() {
		this.LastGeneration = this.System.MapGeneration
	}

	Click(e?: MouseEvent) {
		if (this.IsOpen) {
			this.Close()
		} else {
			this.Open()
		}
	}

	IsValid(): boolean {
		return this.LastGeneration == this.System.MapGeneration
	}

	Open() {
		if (!this.IsValid()) {
			this.BuildElement()
		}

		for (const button of this.System.Buttons) {
			if (button == this) {
				continue
			}

			button.Close()
		}

		this.IsOpen = true
		const rect = this.Button.getBoundingClientRect()
		const top = rect.top + rect.height + window.scrollX + 10
		this.PanelContainer.style.left = `${rect.left + window.scrollY + rect.width + 10}px`
		this.PanelContainer.style.display = 'block'
		this.PanelContainer.style.zIndex = (Number.MAX_SAFE_INTEGER - 100).toString(10)
	}

	Close() {
		this.IsOpen = false
		this.PanelContainer.style.display = 'none'
	}

	onAdd(map: Map): HTMLElement {
		return this.Container
	}

	onRemove(map: Map): void {
		this.Container.remove()
		this.PanelContainer.remove()
	}
}

class MapButton extends MapButtonBase {
	ElementPanel: HTMLDivElement

	constructor(icon: string, system: MapSystem) {
		super(icon, system)

		this.ElementPanel = document.createElement('div')

		const label = document.createElement('h4')
		label.textContent = 'Select Map'

		this.PanelContainer.appendChild(label)
		this.PanelContainer.appendChild(this.ElementPanel)
	}

	BuildElement() {
		super.BuildElement()

		let children: HTMLLabelElement[] = []

		for (const map of this.System.Maps) {
			const label = document.createElement('label')
			label.classList.add('switch')

			const button = document.createElement('input')
			button.type = 'radio'
			button.name = 'MapSelector'
			button.checked = this.System.CurrentMap == map.Data.Name
			button.toggleAttribute('switch', true)
			button.addEventListener('click', () => {
				if (!button.checked || this.System.CurrentMap == map.Data.Name) {
					return
				}

				this.System.SwitchMap(map.Data.Name)
			})

			label.appendChild(button)
			label.append(document.createTextNode(this.System.Core.Lang.L(`WORLDMAP_NAME_${map.Data.Name}`)))
			children.push(label)
		}

		this.ElementPanel.replaceChildren(...children)
	}
}

class LayerButton extends MapButtonBase {
	ElementPanel: HTMLDivElement
	LastMap: string

	constructor(icon: string, system: MapSystem) {
		super(icon, system)

		this.ElementPanel = document.createElement('div')

		const label = document.createElement('h4')
		label.textContent = 'Select Layers'

		this.PanelContainer.appendChild(label)
		this.PanelContainer.appendChild(this.ElementPanel)
		this.LastMap = ''
	}

	IsValid(): boolean {
		return super.IsValid() && this.LastMap == this.System.CurrentMap
	}

	BuildButton(isChecked: boolean, callback: (isChecked: boolean) => any): [HTMLLabelElement, HTMLInputElement] {
		const label = document.createElement('label')
		label.classList.add('switch')

		const button = document.createElement('input')
		button.type = 'checkbox'
		button.checked = isChecked
		button.toggleAttribute('switch', true)
		button.addEventListener('click', () => callback(button.checked))
		label.appendChild(button)

		return [label, button]
	}

	BuildElement() {
		this.LastMap = this.System.CurrentMap

		const groups: Record<string, ([LayerSpecification & MapLayer, MapGroup])[]> = {}
		const groupWeight: Record<string, number> = {}
		for (const mapGroup of this.System.Maps) {
			for (const mapLayerName in mapGroup.Layers) {
				const mapLayer = mapGroup.Layers[mapLayerName]!

				var metaGroup = mapLayer.MetaGroup
				if (metaGroup === null) {
					continue
				}

				if (!(metaGroup in groups)) {
					groups[metaGroup] = []
					groupWeight[metaGroup] = mapLayer.MetaPriority
				}

				groups[metaGroup]!.push([mapLayer, mapGroup])
			}
		}

		let children: HTMLElement[] = []
		const groupKeys = Object.keys(groups).toSorted((a, b) => {
			const weight = (groupWeight[a] ?? 0) - (groupWeight[b] ?? 0)
			return weight == 0 ? a.localeCompare(b) : weight
		})

		for (const metaGroupName of groupKeys) {
			const header = document.createElement('h5')
			const checkboxes: [MapLayer, HTMLInputElement?][] = []
			const [headerLabel, _] = this.BuildButton(true, (isChecked) => {
				for (const [layer, checkbox] of checkboxes) {
					if (checkbox !== undefined) {
						checkbox.checked = isChecked
					}

					if (isChecked) {
						this.System.ShowLayerGroup(layer.Id)
					} else {
						this.System.HideLayerGroup(layer.Id)
					}
				}
			})
			headerLabel.append(document.createTextNode(this.System.Core.Lang.L(metaGroupName).split('\n')[0]!.trim()))
			header.appendChild(headerLabel)
			children.push(header)

			for (const [layer, mapGroup] of groups[metaGroupName]!.toSorted((a, b) => a[0].Priority - b[0].Priority)) {
				if (mapGroup.Data.Name != this.System.CurrentMap) {
					checkboxes.push([layer])
					continue
				}

				const [label, button] = this.BuildButton(!layer.ForceHide, (isChecked) => {
					if (isChecked) {
						this.System.ShowLayerGroup(layer.Id)
					} else {
						this.System.HideLayerGroup(layer.Id)
					}
				})

				if (layer.Icon !== null) {
					const icon = document.createElement('img')
					icon.alt = ''
					icon.src = layer.Icon
					label.append(icon)
				}

				checkboxes.push([layer, button])

				label.append(document.createTextNode(this.System.Core.Lang.L(layer.DisplayName).split('\n')[0]!.trim()))
				children.push(label)
			}
		}

		this.ElementPanel.replaceChildren(...children)
	}
}

export class MapSystem {
	Core: VoreCore
	Maps: MapGroup[]
	MapGeneration: number
	Buttons: MapButtonBase[]
	MapInstance: Map
	LoadedImages: Set<string>
	CurrentMap: string
	Interactive: boolean

	constructor(core: VoreCore, container?: HTMLElement | string, interactive?: boolean) {
		this.Core = core
		this.Interactive = interactive ?? true
		this.MapInstance = new Map({
			container: container ?? 'game-map',
			minZoom: 0,
			maxZoom: 5,
			center: [0, 0],
			zoom: 0,
			attributionControl: false,
			interactive: this.Interactive,
			style: {
				version: 8,
				sprite: [
					{
						id: 'Pal',
						url: core.Data.GetAbsoluteResource('Map/_Sheets/Pal')
					},
					{
						id: 'PalSkin',
						url: core.Data.GetAbsoluteResource('Map/_Sheets/PalSkin')
					},
					{
						id: 'Item',
						url: core.Data.GetAbsoluteResource('Map/_Sheets/Item')
					},
					{
						id: 'Building',
						url: core.Data.GetAbsoluteResource('Map/_Sheets/Building')
					}
				],
				sources: {},
				layers: []
			},
			transformRequest: (url, resourceType) => {
				if (resourceType === 'SpriteImage' || resourceType === 'SpriteJSON') {
					return {
						url: url.replace('@2x', '')
					}
				}
			}
		})

		this.MapInstance.addControl(new AttributionControl({
			compact: true,
			customAttribution: 'Data &copy; <a href="https://www.pocketpair.jp/palworld">Pocketpair</a> | <a href="https://maplibre.org/">MapLibre</a>'
		}), 'bottom-right')

		this.MapInstance.addControl(new AttributionControl({
			compact: true,
			customAttribution: `Palworld Version ${core.Data.Version}`
		}), 'bottom-right')

		this.MapGeneration = 0
		this.Buttons = []
		this.Maps = []
		this.LoadedImages = new Set()
		this.CurrentMap = localStorage.getItem(`Map.Last`) ?? ''
	}

	async Load() {
		if (this.Maps.length > 0) {
			return
		}

		await new Promise<void>((resolve) => {
			const handle = () => {
				if (this.MapInstance.isStyleLoaded()) {
					resolve()
					return
				}

				setTimeout(handle, 100)
			}

			handle()
		})

		const maps = await this.Core.Data.GetData<MapData[]>('WorldMapUIData')

		if (maps !== undefined) {
			for (const map of maps) {
				console.log(`[MapSystem] configuring map ${map.Name}`)

				if (this.CurrentMap.length === 0) {
					const visible = this.Maps.length == 0
					if (visible) {
						this.CurrentMap = map.Name
					}
				}

				this.Maps.push(new MapGroup(this.Core, map, this.MapInstance, this.CurrentMap === map.Name, ''))
				this.MapGeneration++
			}
		}
	}

	async LoadIcon(name: string): Promise<string> {
		if (this.MapInstance.hasImage(name)) {
			return name
		}

		if (!this.LoadedImages.add(name)) {
			return name
		}

		const image = await this.MapInstance.loadImage(this.Core.Data.GetResource(name))
		this.MapInstance.addImage(name, image.data)
		return name
	}

	static ConvertPoint(x: number, y: number, map: MapData): LngLatLike {
		const minX = map.Min.X
		const maxX = map.Max.X
		const minY = map.Min.Y
		const maxY = map.Max.Y

		const normalizedX = (x - minX) / (maxX - minX)
		const normalizedY = (y - minY) / (maxY - minY)

		const lng = (normalizedY * 360) - 180
		const lat = (360 / Math.PI) * Math.atan(Math.exp(Math.PI * (normalizedX * 2 - 1))) - 90

		return [lng, lat]
	}

	PinToLayer(x: number, y: number): MapPoint | null {
		if (this.Maps === undefined) {
			return null
		}

		for (const map of this.Maps) {
			const data = map.Data
			if (x >= data.Min.X && y >= data.Min.Y && data.Max.X >= x && data.Max.Y >= y) {
				return {
					Coordinate: MapSystem.ConvertPoint(x, y, map.Data),
					Group: map,
					Properties: null,
				}
			}
		}

		return null
	}

	SwitchMap(mapId: string) {
		this.MapInstance.setZoom(0)
		for (const map of this.Maps) {
			map.SetVisibility(this.MapInstance, map.Data.Name == mapId)
		}
		this.CurrentMap = mapId
	}

	HideLayerGroup(groupId: string) {
		for (const map of this.Maps) {
			const layerId = `${map.Data.Name}${groupId}Layer`
			const layer = map.Layers[layerId]
			if (layer === undefined) {
				continue
			}

			localStorage.setItem(`Map[${layerId}].Visible`, '0')
			layer.ForceHide = true
			if (map.Data.Name == this.CurrentMap) {
				this.MapInstance.setLayoutProperty(layerId, 'visibility', 'none')
			}
		}
	}

	ShowLayerGroup(groupId: string) {
		for (const map of this.Maps) {
			const layerId = `${map.Data.Name}${groupId}Layer`
			const layer = map.Layers[layerId]
			if (layer === undefined) {
				continue
			}

			localStorage.setItem(`Map[${layerId}].Visible`, '1')
			layer.ForceHide = false
			if (map.Data.Name == this.CurrentMap) {
				this.MapInstance.setLayoutProperty(layerId, 'visibility', 'visible')
			}
		}
	}

	async AsFixed(position: Vector3, zoomLevel: number, iconSettings?: IconSettings): Promise<boolean> {
		const mapPoint = this.PinToLayer(position.X, position.Y)
		if (mapPoint == null) {
			return false
		}

		this.SwitchMap(mapPoint.Group.Data.Name)
		this.MapInstance.jumpTo({
			center: mapPoint.Coordinate,
			zoom: zoomLevel,
		})

		const iconId = iconSettings?.IconId

		if (iconId !== undefined && iconId instanceof String) {
			if (this.MapInstance.getLayer('TemporaryIconLayer')) {
				this.MapInstance.removeLayer('TemporaryIconLayer')
			}

			if (this.MapInstance.getSource('TemporaryIcon')) {
				this.MapInstance.removeSource('TemporaryIcon')
			}

			const source: SourceSpecification = {
				type: 'geojson',
				data: {
					type: 'FeatureCollection',
					features: [{
						type: 'Feature',
						geometry: {
							type: 'Point',
							coordinates: mapPoint.Coordinate as Position
						},
						properties: null
					}],
				},
				cluster: false,
			}

			const layer: LayerSpecification = {
				id: 'TemporaryIconLayer',
				type: 'symbol',
				source: 'TemporaryIcon',
				layout: {
					visibility: 'visible',
					'icon-image': await this.LoadIcon(iconId as string),
					'icon-size': iconSettings?.Size ?? 1.0,
					'icon-allow-overlap': true,
					'icon-ignore-placement': true,
				},
			}

			this.MapInstance.addSource('TemporaryIcon', source)
			this.MapInstance.addLayer(layer)
		}

		return true
	}

	async LoadMapData<T extends UPalActor>(info: MapLayer, iconSettings: IconSettings, visitor?: (element: T, map: MapSystem, point: MapPoint) => Promise<boolean>) {
		const points = await this.Core.Data.GetData<T[]>(`Map/${info.Id}`)

		if (points === undefined) {
			return
		}

		const buckets: Record<string, MapPoints> = {}

		for (const point of points) {
			const mapPoint = this.PinToLayer(point.Position.X, point.Position.Y)

			if (mapPoint === null) {
				continue
			}

			if (visitor !== undefined && !await visitor(point, this, mapPoint)) {
				continue
			}

			const mapName = mapPoint.Group.Data.Name
			if (!(mapName in buckets)) {
				buckets[mapName] = {
					Group: mapPoint.Group,
					Points: []
				}
			}

			buckets[mapName]!.Points.push(mapPoint)
		}

		for (const bucket of Object.values(buckets)) {
			bucket.Group.CreateSource(this.MapInstance, info, iconSettings)
			bucket.Group.AddPointToSource(this.MapInstance, info.Id, bucket.Points)
			this.MapGeneration++
		}
	}

	async LoadMapSubData<T extends UPalActor>(type: string, selector: (element: T) => string, iconVisitor: (name: string) => Promise<[IconSettings, MapLayer]>, visitor?: (element: T, map: MapSystem, point: MapPoint) => Promise<boolean>, preparse?: (elements: T[]) => Promise<void>) {
		const points = await this.Core.Data.GetData<T[]>(`Map/${type}`)

		if (points === undefined) {
			return
		}

		const buckets: Record<string, Record<string, MapPoints>> = {}

		for (const point of points) {
			const mapPoint = this.PinToLayer(point.Position.X, point.Position.Y)

			if (mapPoint === null) {
				continue
			}

			if (visitor !== undefined && !await visitor(point, this, mapPoint)) {
				continue
			}

			const mapName = mapPoint.Group.Data.Name
			if (!(mapName in buckets)) {
				buckets[mapName] = {}
			}

			const bucketName = selector(point)

			if (!(bucketName in buckets[mapName]!)) {
				buckets[mapName]![bucketName] = {
					Group: mapPoint.Group,
					Points: []
				}
			}

			buckets[mapName]![bucketName]!.Points.push(mapPoint)
		}

		for (const bucketGroup of Object.values(buckets)) {
			for (const bucketName in bucketGroup) {
				const bucket = bucketGroup[bucketName]!
				const [iconSetting, info] = await iconVisitor(bucketName)
				info.Id = bucketName
				bucket.Group.CreateSource(this.MapInstance, info, iconSetting)
				bucket.Group.AddPointToSource(this.MapInstance, bucketName, bucket.Points)
				this.MapGeneration++
			}
		}
	}

	SpawnGroupToIcon(spawnGroups: FPalSpawnerGroupInfo[] | undefined, point: MapPoint, filter: string = 'BOSS_'): boolean {
		if (spawnGroups === undefined) {
			return false
		}

		for (const spawnGroup of spawnGroups) {
			const palList = spawnGroup.PalList

			if (palList === undefined) {
				continue
			}

			for (const pal of palList) {
				if (pal.PalId.startsWith(filter)) {
					const param = this.Core.Pal.PalMonsterParameters[pal.PalId]
					if (param === undefined) {
						continue
					}

					let tribe = param.Tribe
					if (tribe == 'VolcanicMonster') {
						tribe = 'Volcanicmonster'
					} else if (tribe == 'BadCatgirl') {
						tribe = 'BadCatGirl'
					} else if (tribe == 'KingAlpaca_Ice') {
						tribe = 'KingAlpaca_ice'
					}

					point.Properties = {
						icon: `Pal:${tribe}`
					}

					return true
				}
			}
		}

		return false
	}

	async LoadAll() {
		this.MapInstance.addControl(new NavigationControl({
			visualizePitch: false,
			visualizeRoll: false,
			showZoom: true,
			showCompass: false
		}), 'top-left')

		const buttons = [
			new MapButton('/Feather/map.svg', this),
			new LayerButton('/Feather/layers.svg', this)
		]

		for (const button of buttons) {
			this.Buttons.push(button)
			this.MapInstance.addControl(button, 'top-left')
		}

		let tasks: Promise<void>[] = []

		tasks.push(this.LoadMapData<UPalLevelObjectUnlockableFastTravelPoint>(
			{
				ForceHide: false,
				Id: 'FastTravelPoint',
				DisplayName: 'MAP_FILTER_FT',
				Icon: this.Core.Data.GetResource('UI/InGame/T_icon_compass_FTUnlockMap'),
				MetaGroup: 'POI',
				MetaPriority: 0,
				Priority: 0,
			},
			{
				IconId: ['get', 'icon'],
				Clump: false,
				Size: 0.5,
			},
			async (element, map, point) => {
				point.Properties = {}
				if (element.UnlockMapMaskTexture !== null) {
					point.Properties['icon'] = await map.LoadIcon('UI/InGame/T_icon_compass_FTUnlockMap')
				} else {
					point.Properties['icon'] = await map.LoadIcon('UI/InGame/T_icon_compass_FTtower')
				}
				return true
			}))

		tasks.push(this.LoadMapData<UPalLevelObjectActor>(
			{
				ForceHide: false,
				Id: 'DungeonPointMarker',
				DisplayName: 'Dungeon',
				Icon: this.Core.Data.GetResource('UI/InGame/T_icon_compass_dungeon'),
				MetaGroup: 'POI',
				MetaPriority: 0,
				Priority: 5,
			},
			{
				IconId: await this.LoadIcon('UI/InGame/T_icon_compass_dungeon'),
				Clump: false,
				Size: 0.35,
			},
			async (element, map, point) => {
				return true
			}))

		tasks.push(this.LoadMapData<UPalDungeonFixedEntrance>(
			{
				ForceHide: false,
				Id: 'DungeonFixedEntrance',
				DisplayName: 'Sealed Realm',
				Icon: this.Core.Data.GetResource('UI/InGame/T_icon_compass_06'),
				MetaGroup: 'Boss',
				MetaPriority: 2,
				Priority: 10,
			},
			{
				IconId: ['get', 'icon'],
				Clump: false,
				Size: 0.5,
			},
			async (element, map, point) => {
				const spawnerStandard = element.Spawner as UPalSpawnerStandard
				if (spawnerStandard.SpawnGroups === undefined) {
					return false
				}

				return this.SpawnGroupToIcon(spawnerStandard.SpawnGroups, point)
			}))

		tasks.push(this.LoadMapData<UPalNPCSpawnerBase>(
			{
				ForceHide: false,
				Id: 'NPCSpawner',
				DisplayName: 'Bounty',
				Icon: this.Core.Data.GetResource('UI/InGame/T_icon_compass_Bounty'),
				MetaGroup: 'Boss',
				MetaPriority: 2,
				Priority: 5,
			},
			{
				IconId: await this.LoadIcon('UI/InGame/T_icon_compass_Bounty'),
				Clump: false,
				Size: 0.5,
			},
			async (element, map, point) => {
				return element.IsBossSpawner
			}))

		tasks.push(this.LoadMapData<UPalSpawnerStandard>(
			{
				ForceHide: false,
				Id: 'PalSpawner',
				DisplayName: 'Field Boss',
				Icon: this.Core.Data.GetResource('UI/InGame/T_icon_enemy_strong'),
				MetaGroup: 'Boss',
				MetaPriority: 2,
				Priority: 0,
			},
			{
				IconId: ['get', 'icon'],
				Clump: false,
				Size: 0.4,
			},
			async (element, map, point) => {
				if (element.SpawnerType != PalSpawnedCharacterType[PalSpawnedCharacterType.FieldBoss]) {
					return false
				}

				return this.SpawnGroupToIcon(element.SpawnGroups, point)
			}))

		tasks.push(this.LoadMapData<UPalMapObjectSpawnerPalEgg>(
			{
				ForceHide: false,
				Id: 'MapObjectPalEggSpawner',
				DisplayName: 'Help_34',
				Icon: this.Core.Data.GetResource('UI/InGame/T_icon_compass_10'),
				MetaGroup: 'POI',
				MetaPriority: 0,
				Priority: 13,
			},
			{
				IconId: ['get', 'icon'],
				Clump: true,
				Size: 0.5,
			},
			async (element, map, point) => {
				point.Properties = {
					icon: this.Core.Data.GetResource('UI/InGame/T_icon_compass_10')
				}

				const keys = Object.keys(PalElementType)
				const lottery = element.SpawnPalEggLottery

				if (lottery !== undefined) {
					const types: Record<string, number> = {}

					for (const spawnEgg of lottery) {
						const param = this.Core.Pal.PalMonsterParameters[spawnEgg.PalMonsterId]
						if (param === undefined) {
							continue
						}

						if (param.ElementType1 == null) {
							continue
						}

						types[param.ElementType1] = (types[param.ElementType1] ?? 0) + spawnEgg.Weight
					}

					let mostLikely = 'None'
					let chance = 0
					for (const type in types) {
						if (types[type]! > chance) {
							mostLikely = type
							chance = types[type]!
						}
					}

					point.Properties.icon = `Item:PalEgg_${mostLikely}_01`
				}
				return true
			}))

		tasks.push(this.LoadMapData<UPalLevelObjectActor>(
			{
				ForceHide: false,
				Id: 'SupplySpawner',
				DisplayName: 'MAPOBJECT_NAME_MeteorFall',
				Icon: this.Core.Data.GetResource('UI/InGame/T_icon_compass_16'),
				MetaGroup: 'POI',
				MetaPriority: 0,
				Priority: 30,
			},
			{
				IconId: await this.LoadIcon('UI/InGame/T_icon_compass_16'),
				Clump: true,
				Size: 0.5,
			},
			async (element, map, point) => {
				return true
			}))

		tasks.push(this.LoadMapData<UPalItemPickup>(
			{
				ForceHide: false,
				Id: 'ItemPickup',
				DisplayName: 'Puzzle Tower',
				Icon: this.Core.Data.GetResource('ItemIcon/T_itemicon_Accessory_Otomo_PalConfidence_Increase_1'),
				MetaGroup: 'POI',
				MetaPriority: 0,
				Priority: 40,
			},
			{
				IconId: ['get', 'icon'],
				Clump: false,
				Size: 0.4,
			},
			async (element, map, point) => {
				const pickupData = this.Core.Pal.ItemPickups[element.RowName]
				if (pickupData === undefined) {
					return false
				}

				let recipeId = pickupData.Item_01_Id
				if (recipeId === null) {
					return false
				}

				if (recipeId in this.Core.Pal.UnlockItemIDLookup) {
					recipeId = this.Core.Pal.UnlockItemIDLookup[recipeId]!
				}

				let itemId = recipeId
				if (recipeId in this.Core.Pal.ItemRecipes) {
					itemId = this.Core.Pal.ItemRecipes[recipeId]!.Product_Id
				}

				let item = this.Core.Pal.Items[itemId]
				if (item === undefined) {
					return false
				}

				point.Properties = {
					icon: `Item:${item.IconName}`
				}

				return true
			}))


		tasks.push(this.LoadMapData<UPalLevelObjectActor>(
			{
				ForceHide: false,
				Id: 'Note',
				DisplayName: 'NOTE_TITLE',
				Icon: this.Core.Data.GetResource('UI/IngameMenu/Buildup/T_icon_Buildup_Player_10'),
				MetaGroup: 'POI',
				MetaPriority: 0,
				Priority: 4,
			},
			{
				IconId: await this.LoadIcon('UI/IngameMenu/Buildup/T_icon_Buildup_Player_10'),
				Clump: false,
				Size: 0.5,
			},
			async (element, map, point) => {
				return true
			}))

		tasks.push(this.LoadMapData<UPalLevelObjectActor>(
			{
				ForceHide: false,
				Id: 'FishingSpotArea',
				DisplayName: 'CATEGORY_TYPE_UI_FishingPond',
				Icon: this.Core.Data.GetResource('UI/Fishing/T_Fishing_PowerGauge_Mark_1'),
				MetaGroup: 'POI',
				MetaPriority: 0,
				Priority: 15,
			},
			{
				IconId: await this.LoadIcon('UI/Fishing/T_Fishing_FishPondIcon'),
				Clump: true,
				Size: 0.35,
			},
			async (element, map, point) => {
				return true
			}))

		tasks.push(this.LoadMapData<UPalLevelObjectActor>(
			{
				ForceHide: false,
				Id: 'BaseCampPopularArea',
				DisplayName: 'MAP_FILTER_CAMP',
				Icon: this.Core.Data.GetResource('UI/InGame/T_icon_compass_camp'),
				MetaGroup: 'POI',
				MetaPriority: 0,
				Priority: 20,
			},
			{
				IconId: await this.LoadIcon('UI/InGame/T_icon_compass_camp'),
				Clump: false,
				Size: 0.35,
			},
			async (element, map, point) => {
				return true
			}))

		tasks.push(this.LoadMapData<UPalLevelObjectWarpPointDestination>(
			{
				ForceHide: false,
				Id: 'WarpPointDestination',
				DisplayName: 'MAP_INFO_TELEPORT',
				Icon: this.Core.Data.GetResource('UI/InGame/T_icon_compass_Teleport'),
				MetaGroup: 'POI',
				MetaPriority: 0,
				Priority: 2,
			},
			{
				IconId: await this.LoadIcon('UI/InGame/T_icon_compass_Teleport'),
				Clump: false,
				Size: 0.5,
			},
			async (element, map, point) => {
				return true
			}))

		tasks.push(this.LoadMapData<UPalLevelObjectWarpPointDestination>(
			{
				ForceHide: false,
				Id: 'RecoverParty',
				DisplayName: 'ADDITIONAL_EFFECT_Recovery',
				Icon: this.Core.Data.GetResource('UI/InGame/T_icon_sleeping'),
				MetaGroup: 'POI',
				MetaPriority: 0,
				Priority: 3,
			},
			{
				IconId: await this.LoadIcon('UI/InGame/T_icon_sleeping'),
				Clump: false,
				Size: 0.5,
			},
			async (element, map, point) => {
				return true
			}))

		tasks.push(this.LoadMapData<UPalBossTower>(
			{
				ForceHide: false,
				Id: 'BossTower',
				DisplayName: 'MAP_FILTER_BOSSTOWER',
				Icon: this.Core.Data.GetResource('UI/InGame/T_icon_compass_tower'),
				MetaGroup: 'POI',
				MetaPriority: 0,
				Priority: 1,
			},
			{
				IconId: ['get', 'icon'],
				Size: ['get', 'size'],
				Clump: false,
			},
			async (element, map, point) => {
				point.Properties = {}
				if (element.IsStandaloneBossTower) {
					if (element.BossType == 'WorldTreeBoss') {
						point.Properties['icon'] = await map.LoadIcon('UI/InGame/T_icon_compass_BossGate')
					} else {
						point.Properties['icon'] = await map.LoadIcon('UI/InGame/T_icon_compass_KeyBoss')
					}
					point.Properties['size'] = 0.5
				} else {
					point.Properties['icon'] = await map.LoadIcon('UI/InGame/T_icon_compass_tower')
					point.Properties['size'] = 0.35
				}
				return true
			}))

		tasks.push(this.LoadMapSubData<ULevelObjectRelic>(
			'LevelObjectRelic',
			(element) => {
				return element.RelicType
			},
			async (name) => {
				const relicType = Object.values(PalRelicType).indexOf(name)
				const id = relicType == 0 ? 'ITEM_NAME_Relic' : `ITEM_NAME_Relic_${relicType.toString(10).padStart(2, '0')}`
				const iconId = relicType == 0 ? 'Relic' : `Relic_${relicType.toString(10).padStart(2, '0')}`
				const icon = `ItemIcon/T_itemicon_${iconId}`
				return [{
					IconId: `Item:${iconId}`,
					Clump: true,
					Size: 0.5,
				}, {
					ForceHide: false,
					Id: `LevelObjectRelic${name}`,
					DisplayName: id,
					Icon: this.Core.Data.GetResource(icon),
					MetaGroup: 'Help_37',
					MetaPriority: 5,
					Priority: relicType,
				}]
			},
			async (element, map, point) => {
				return true
			}))

		tasks.push(this.LoadMapSubData<UMapObjectSpawner>(
			'MapObjectSpawner',
			(element) => {
				return element.MapObjectId
			},
			async (name) => {
				const item = MapObjectMapping[name as keyof typeof MapObjectMapping]!
				const index = Object.keys(item).indexOf(name)
				const icon = `ItemIcon/T_itemicon_Material_${item[0]!}`
				const id = item[1] ?? `MAPOBJECT_NAME_${name}`
				return [{
					IconId: `Item:${item[0]!}`,
					Clump: true,
					Size: 0.5,
				}, {
					ForceHide: false,
					Id: `MapObjectSpawner${name}`,
					DisplayName: id,
					Icon: this.Core.Data.GetResource(icon),
					MetaGroup: 'ITEM_FILTER_ORE',
					MetaPriority: 10,
					Priority: index,
				}]
			},
			async (element, map, point) => {
				return element.Position.Z > -20000 && element.MapObjectId in MapObjectMapping
			}))

		Promise.allSettled(tasks)
	}
}
